import {
  Grid,
  CardHeader,
  CardContent,
  Box,
  TextField,
  Avatar,
  Button,
  IconButton,
  Typography,
  FormHelperText,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Form, FormikProvider, useFormik } from "formik";
import { IMAGE_BASE } from "../../utils/constants";
import LoadingButton from "@mui/lab/LoadingButton";
import CardLayout from "../../layouts/CardLayout";
import { Helmet } from "react-helmet-async";
import { toast } from "react-toastify";
import { RemoveCircle } from "@mui/icons-material";
import errorHandler from "../../utils/errorHandler";
import { BrandSchema } from "../../utils/Schema";
import { brandServices } from "../../services/AllServices";
import { trimFormValues } from "../../utils/helpers";

// Styled components for image preview container and remove button
const PreviewContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  width: 100,
  height: 100,
  borderRadius: theme.shape.borderRadius,
  overflow: "hidden",
  boxShadow: theme.shadows[3],
  cursor: "pointer",
  "&:hover .remove-btn": {
    opacity: 1,
  },
}));

const RemoveButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  top: 4,
  right: 4,
  backgroundColor: theme.palette.error.main,
  color: theme.palette.common.white,
  padding: 4,
  opacity: 0.8,
  transition: "opacity 0.3s",
  "&:hover": {
    backgroundColor: theme.palette.error.dark,
    opacity: 1,
  },
}));

function AddEditMakes() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState();

  // Single existing image (filename/string)
  const [existingImage, setExistingImage] = useState("");
  // Newly selected image as a File
  const [selectedImage, setSelectedImage] = useState(null);
  // Single preview URL (either server URL or local object URL)
  const [imagePreview, setImagePreview] = useState("");

  const fileInputRef = useRef(null);
  const { id } = useParams();

  const fetchTaskData = useCallback(async () => {
    if (id) {
      setLoading(true);
      try {
        const response = await brandServices.getBrandById({ id });
        console.log(response, "response");
        
        const resData = response?.data?.data || {};
        const existingImg = resData?.brandLogo || "";

        setExistingImage(existingImg);
        setSelectedImage(null);
        setImagePreview(existingImg ? IMAGE_BASE + existingImg : "");
        setData(resData);
      } catch (error) {
        errorHandler(error);
      } finally {
        setLoading(false);
      }
    }
  }, [id]);

  useEffect(() => {
    fetchTaskData();
  }, [fetchTaskData]);

  const formik = useFormik({
    enableReinitialize: true,
    validationSchema: BrandSchema,
    initialValues: {
      name: data?.name || "",
      description: data?.description || "",
      brandLogo: data?.brandLogo || "",
      status: data?.status || 1,
    },
    onSubmit: async (values) => {
      setLoading(true);
      try {
        // Trim all string values in the form values
        const trimmedValues = trimFormValues(values);
        
        const formData = new FormData();
        if (id) formData.append("id", id);
        formData.append("name", trimmedValues.name);
        formData.append("description", trimmedValues.description);
        formData.append("status", trimmedValues.status);
        
        // Append only one brandLogo (file if selected, otherwise existing string)
        if (selectedImage) {
          formData.append("brandLogo", selectedImage);
        } else if (existingImage) {
          formData.append("brandLogo", existingImage);
        }
    
        const response = id
          ? await brandServices.edit(formData)
          : await brandServices.add(formData);
    
        toast.success(response?.data?.message, { toastId: "success" });
        navigate(-1);
      } catch (error) {
        errorHandler(error);
      } finally {
        setLoading(false);
      }
    },
    
  });

  const handleImageChange = (event) => {
    const file = event.currentTarget.files && event.currentTarget.files[0];
    const validTypes = ["image/jpeg", "image/png", "image/gif"]; 
    if (!file) return;
    if (!validTypes.includes(file.type)) {
      toast.error("Invalid file type. Only JPG, PNG, and GIF are allowed.");
      event.target.value = null;
      return;
    }

    setSelectedImage(file);
    setExistingImage("");
    setImagePreview(URL.createObjectURL(file));
    formik.setFieldValue("brandLogo", file);

    event.target.value = null;
  };

  const handleRemoveImage = () => {
    setExistingImage("");
    setSelectedImage(null);
    setImagePreview("");
    formik.setFieldValue("brandLogo", null);
  };

  return (
    <Box>
      <Helmet>
        <title>{id ? "Edit" : "Add"} Brand</title>
      </Helmet>
      <CardLayout>
        <CardHeader title={`${id ? "Edit" : "Add"} Brand`} />
        <CardContent>
          <FormikProvider value={formik}>
            <Form autoComplete="off" noValidate onSubmit={formik.handleSubmit}>
              <Grid container spacing={3}>

                {/* Image Upload Section */}
                <Grid size={{ xs: 12 }} md={12}>
                  <input
                    accept="image/*"
                    type="file"
                    ref={fileInputRef}
                    onChange={handleImageChange}
                    style={{ display: "none" }}
                  />
                  <Box
                    mb={1}
                    display="flex"
                    gap={2}
                    alignItems="center"
                  >
                    {!imagePreview && (
                      <Typography variant="subtitle2" color="text.secondary" sx={{ width: "100%" }}>
                        No image uploaded yet
                      </Typography>
                    )}
                    {imagePreview && (
                      <PreviewContainer
                        title="Click to change image"
                        onClick={() => fileInputRef.current.click()}
                      >
                        <Avatar
                          src={imagePreview}
                          alt={`preview`}
                          variant="rounded"
                          sx={{ width: "100%", height: "100%" }}
                        />
                        <RemoveButton
                          size="small"
                          aria-label="Remove image"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveImage();
                          }}
                          className="remove-btn"
                        >
                          <RemoveCircle fontSize="small" />
                        </RemoveButton>
                      </PreviewContainer>
                    )}
                  </Box>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => fileInputRef.current.click()}
                    sx={{ textTransform: "none" }}
                  >
                    {imagePreview ? "Change Image" : id ? "Edit Image" : "Upload Image"}
                  </Button>
                  {formik.touched.brandLogo && formik.errors.brandLogo && (
                    <FormHelperText error>{formik.errors.brandLogo}</FormHelperText>
                  )}
                </Grid>

                {/* Category Name */}
                <Grid size={{ xs: 12 }} md={12}>
                  <TextField
                    fullWidth
                    label="Brand Name"
                    value={formik.values.name}
                    onChange={formik.handleChange}
                    required
                    name="name"
                    error={Boolean(formik.touched.name && formik.errors.name)}
                    helperText={formik.touched.name && formik.errors.name}
                    inputProps={{ maxLength: 50 }}
                    placeholder="e.g., SUVs, Sedans, Trucks, Motorcycles"
                  />
                </Grid>

                {/* Description */}
                <Grid size={{ xs: 12 }} md={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    multiline
                    rows={3}
                    
                    value={formik.values.description}
                    onChange={formik.handleChange}
                    name="description"
                    error={Boolean(formik.touched.description && formik.errors.description)}
                    helperText={formik.touched.description && formik.errors.description}
                    inputProps={{ maxLength: 200 }}
                    placeholder="Brief description of the Brand"
                  />
                </Grid>

                {/* Submit Button */}
                <Grid size={{ xs: 12 }}>
                  <Box mt={3} width="100%">
                    <LoadingButton
                      fullWidth
                      size="large"
                      type="submit"
                      variant="contained"
                      loading={loading}
                    >
                      {id ? "Update" : "Add"} Brand
                    </LoadingButton>
                  </Box>
                </Grid>

              </Grid>
            </Form>
          </FormikProvider>
        </CardContent>
      </CardLayout>
    </Box>
  );
}

export default AddEditMakes;
